
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import Logo from "./header/Logo";
import DesktopNavigation from "./header/DesktopNavigation";
import ContactInfo from "./header/ContactInfo";
import CTAButton from "./header/CTAButton";

import MobileNavigation from "./header/MobileNavigation";

interface HeaderProps {
  onOpenForm: () => void;
  forceScrolled?: boolean;
}

const Header = ({ onOpenForm, forceScrolled = false }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(forceScrolled);
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  // Check if it's currently open (Monday-Friday, 9AM-8PM)
  const checkIfCurrentlyOpen = () => {
    const now = new Date();
    const day = now.getDay(); // 0 is Sunday, 1-5 is Monday-Friday, 6 is Saturday
    const hour = now.getHours();
    
    return day >= 1 && day <= 5 && hour >= 9 && hour < 20;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(forceScrolled || false);
      }
    };

    setIsOpen(checkIfCurrentlyOpen());
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [forceScrolled]);

  return (
    <header
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/90 backdrop-blur-md shadow-sm py-3 mx-0 top-0"
          : "bg-transparent py-4 md:py-6 px-4 md:mx-8 lg:mx-12 top-0 md:mt-4 lg:mt-6"
      }`}
    >
      <div className={`max-w-5xl mx-auto flex justify-between items-center px-4 md:px-6 ${
        isScrolled ? "" : "md:rounded-full md:bg-white/10 md:backdrop-blur-sm md:shadow-sm md:py-2"
      }`}>
        <div className="flex items-center">
          <Logo isScrolled={isScrolled} />
        </div>

        {/* Desktop Navigation */}
        {!isMobile && <DesktopNavigation isScrolled={isScrolled} />}

        <div className="flex items-center gap-2">
          {/* CTA Buttons - Desktop only */}
          {!isMobile && (
            <>
              <CTAButton isScrolled={isScrolled} onClick={onOpenForm} />
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <MobileNavigation 
              isScrolled={isScrolled}
              isOpen={isOpen}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              onOpenForm={onOpenForm}
            />
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
